import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics, isSupported as analyticsIsSupported } from "firebase/analytics";

// Use emulators only when explicitly enabled AND running on localhost
const isBrowser = typeof window !== 'undefined';
const isLocalHost = isBrowser && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const isLocal = import.meta.env.VITE_USE_EMULATORS === 'true' && isLocalHost;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

export const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const app = initializeApp(firebaseConfig);
let analytics = null;
async function initAnalytics() {
  try {
    if (firebaseConfig.measurementId && isBrowser) {
      const supported = await analyticsIsSupported();
      if (supported) {
        analytics = getAnalytics(app);
      }
    }
  } catch {
    analytics = null;
  }
}
initAnalytics();

const auth = getAuth(app);
const db = getDatabase(app);
const firestore = getFirestore(app);
const functions = getFunctions(app, isLocal ? undefined : 'us-central1');
const storage = getStorage(app);

console.log('🔧 Firebase Config Debug:');
console.log('  VITE_USE_EMULATORS:', import.meta.env.VITE_USE_EMULATORS);
console.log('  isLocalHost:', isLocalHost);
console.log('  isLocal:', isLocal);
console.log('  window.location.hostname:', isBrowser ? window.location.hostname : 'N/A');

if (isLocal) {
  console.log("✅ Connecting to Firebase Emulators with test project config.");
  try {
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    console.log("✅ Auth emulator connected");
  } catch (error) {
    if (!error.message.includes('already')) {
      console.error('❌ Auth emulator connection failed:', error);
    }
  }

  try {
    connectDatabaseEmulator(db, "localhost", 9000);
    console.log("✅ Database emulator connected to localhost:9000");
  } catch (error) {
    console.error('❌ Database emulator connection failed:', error);
  }

  try {
    connectFirestoreEmulator(firestore, "localhost", 8080);
    console.log("✅ Firestore emulator connected");
  } catch (error) {
    console.error('❌ Firestore emulator connection failed:', error);
  }

  try {
    connectFunctionsEmulator(functions, "localhost", 5001);
    console.log("✅ Functions emulator connected");
  } catch (error) {
    console.error('❌ Functions emulator connection failed:', error);
  }

  try {
    connectStorageEmulator(storage, "localhost", 9199);
    console.log("✅ Storage emulator connected");
  } catch (error) {
    console.error('❌ Storage emulator connection failed:', error);
  }
} else {
  console.log("⚠️ NOT connecting to emulators - using production Firebase");
  console.log("  Reason: VITE_USE_EMULATORS=" + import.meta.env.VITE_USE_EMULATORS + ", isLocalHost=" + isLocalHost);
}

export { app, auth, db, firestore, functions, storage, analytics, isLocal }; 